import React, { useRef, useEffect, useState, Suspense } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { Vector3, Mesh } from 'three';
import * as THREE from 'three';
import { useRaycast } from '@/components/viewer/scene/RaycastManager';
import useEquirectangularStore from '@/store/viewer';
import usePanoramaStore from '@/store/panorama';
import { palette } from '@/modules/defines/styles';
import type { Panorama } from '@/modules/defines/interface';
import { logClickMoveButton } from '@/utils/amplitude';
const HotspotShapeWidget = React.lazy(
  () => import('@/components/viewer/scene/HotspotButton')
);

interface ResponsivePositionMarkerProps {
  position?: [number, number, number];
  viewerKey: string;
  style?: React.CSSProperties;
  buttonText?: string;
  connect?: boolean;
  external?: boolean;
  externalFunction?: (value: string) => void;
  panorama?: Panorama;
  buttonType?: string;
  pathId?: number[];
}

export default function ResponsivePositionMarker({
  position = [0, 0, 0],
  viewerKey,
  buttonText,
  connect,
  external,
  externalFunction,
  panorama,
  buttonType,
  pathId,
}: ResponsivePositionMarkerProps): JSX.Element {
  const objectRef = useRef<Mesh>(null);
  const { camera, gl } = useThree();
  const { hoveredObject } = useRaycast();
  const targetPosition = useRef(new Vector3());
  const startPosition = useRef(new Vector3());
  const moveProgress = useRef(0);
  const isMoving = useRef(false);
  const startViewerKeyRef = useRef<string | null>(null);
  const [reverse, setReverse] = useState(false);
  const duration = 0.5;
  const hasCompletedMovement = useRef(false);
  const updateViewer = useEquirectangularStore((state) => state.updateViewer);
  const viewers = useEquirectangularStore((state) => state.viewers);
  const currentViewerKey = useEquirectangularStore(
    (state) => state.currentViewerKey
  );
  const setCurrentViewerKey = useEquirectangularStore(
    (state) => state.setCurrentViewerKey
  );
  const pid = usePanoramaStore((state) => state.id);
  const setCurrentPosition = usePanoramaStore(
    (state) => state.setCurrentPosition
  );
  const hotspotButtonData = usePanoramaStore(
    (state) => state.hotspotButtonData
  );
  const setId = usePanoramaStore((state) => state.setId);
  const [show, setShow] = useState(true);
  const [scale, setScale] = useState(0.3);
  const [_groupZPosition, setGroupZPosition] = useState(1);

  const isHovered = hoveredObject === objectRef.current;

  // 마커의 호버 상태에 따라 색상 변경
  useEffect(() => {
    if (objectRef.current) {
      // console.log(objectRef.current);
      const material = objectRef.current.material as THREE.MeshBasicMaterial;
      const newColor = isHovered ? 'hotpink' : 'orange';
      if (material && material.color.getStyle() !== newColor) {
        material.color.set(newColor);
        material.needsUpdate = true;
      }
    }
  }, [isHovered]);

  // 이동 중일 때 클릭 이벤트 차단
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (isMoving.current) e.stopPropagation();
    };
    gl.domElement.addEventListener('click', handleClick);
    return () => {
      gl.domElement.removeEventListener('click', handleClick);
    };
  }, [gl]);

  // 마커 클릭 시 이동 시작
  const handleMarkerClick = () => {
    console.log('CLICKED >>>>>');
    logClickMoveButton();
    if (connect) {
      const [x, y, z] = viewers[viewerKey].position!; // 배열 구조 분해
      targetPosition.current.set(x, y, z);
      startPosition.current.copy(camera.position);
      moveProgress.current = 0;
      isMoving.current = true;
      hasCompletedMovement.current = false;
      startViewerKeyRef.current = currentViewerKey;

      const distance = targetPosition.current.distanceTo(camera.position);
      updateViewer(viewerKey, {
        visible: true,
        radius: distance * 2,
        opacity: 1,
      });
      updateViewer(startViewerKeyRef.current!, {
        visible: true,
        radius: distance * 2,
        opacity: 1,
      });
    } else if (external && externalFunction) {
      externalFunction(viewerKey);
    } else {
      if (isMoving.current || !isHovered) return;
      // const { x, y, z } = objectRef.current!.position;

      const result_viewer = viewers[viewerKey];
      targetPosition.current.set(
        result_viewer.position![0],
        result_viewer.position![1],
        result_viewer.position![2]
      );
      startPosition.current.copy(camera.position);
      moveProgress.current = 0;
      isMoving.current = true;
      hasCompletedMovement.current = false;
      startViewerKeyRef.current = currentViewerKey;

      const distance = targetPosition.current.distanceTo(camera.position);
      updateViewer(viewerKey, {
        visible: true,
        radius: distance * 2,
        opacity: 1,
      });
      updateViewer(startViewerKeyRef.current!, {
        visible: true,
        radius: distance * 2,
        opacity: 1,
      });
    }
  };

  const easeInOutQuad = (t: number): number =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;

  const adjustViewerOpacity = () => {
    const startViewerKey = startViewerKeyRef.current;
    if (startViewerKey && startViewerKey !== viewerKey) {
      const distance = camera.position.distanceTo(startPosition.current);
      const newOpacity = distance >= 1.5 ? 0 : 1.5 - distance;
      updateViewer(startViewerKey, { opacity: newOpacity });
    }
  };

  const handleCameraMove = (delta: number) => {
    if (isMoving.current) {
      moveProgress.current += delta / duration;
      const t = Math.min(moveProgress.current, 1);
      const easedT = easeInOutQuad(t);
      camera.position.lerpVectors(
        startPosition.current,
        targetPosition.current,
        easedT
      );

      adjustViewerOpacity();

      if (moveProgress.current >= 1) finalizeCameraMove();
    }
  };

  const finalizeCameraMove = () => {
    if (hasCompletedMovement.current) return;

    camera.position.copy(targetPosition.current);
    isMoving.current = false;
    hasCompletedMovement.current = true;

    const startViewerKey = startViewerKeyRef.current;
    if (startViewerKey && startViewerKey !== viewerKey) {
      updateViewer(startViewerKey, { visible: false, radius: 0.1, opacity: 0 });
    }
    setId(parseInt(viewerKey.split('-')[1]));
    const result_viewer = viewers[viewerKey];
    setCurrentPosition([
      result_viewer.position![0],
      result_viewer.position![1],
      result_viewer.position![2],
    ]);
    setCurrentViewerKey(viewerKey);
    updateViewer(viewerKey, { visible: true, radius: 1, opacity: 1 });
  };

  const adjustOpacityBasedOnDistance = () => {
    if (objectRef.current) {
      const spherePosition =
        connect || external
          ? objectRef.current.getWorldPosition(new Vector3())
          : objectRef.current
              .getWorldPosition(new Vector3())
              .setY(camera.position.y);
      const distance = camera.position.distanceTo(spherePosition);

      const minDistance = 0.01;
      const viewDistanceSquared =
        viewers[currentViewerKey!].panorama?.viewDistanceSquared;
      // const viewDistanceSquared = panorama ? panorama.viewDistanceSquared : 4.0;
      const tempMax = viewDistanceSquared ?? 4.0;
      const maxDistance = Math.sqrt(tempMax);

      const material = objectRef.current.material as THREE.MeshBasicMaterial;

      if (material) {
        material.needsUpdate = true;
        if (connect || external) {
          setShow(true);
        } else {
          if (distance >= minDistance && distance < maxDistance) {
            const tempId = parseInt(viewerKey.split('-')[1]);
            let tempShow = false;

            Object.values(hotspotButtonData).forEach((item) => {
              if (tempId === item.id) {
                tempShow = true;
              }
            });
            setShow(tempShow);
          } else {
            setShow(false);
          }
        }
      }
    }
  };

  useEffect(() => {
    if (objectRef.current) {
      const spherePosition = objectRef.current.getWorldPosition(new Vector3());
      const distance = camera.position.distanceTo(spherePosition);
      // 스케일 값을 거리 기반으로 설정 (예: 가까울수록 커지고 멀수록 작아짐)
      const maxScale = 0.5; // 최대 스케일
      const minScale = 0.3; // 최소 스케일
      const scaleFactor = THREE.MathUtils.clamp(
        1 / distance,
        minScale,
        maxScale
      );
      setScale(scaleFactor);

      switch (true) {
        case distance > 50:
          setGroupZPosition(10);
          break;
        case distance > 40:
          setGroupZPosition(8);
          break;
        case distance > 30:
          setGroupZPosition(6);
          break;
        case distance > 20:
          setGroupZPosition(4);
          break;
        default:
          setGroupZPosition(2);
      }
    }
  }, [camera.position, position]);

  useFrame((_state, delta) => {
    adjustOpacityBasedOnDistance();
    handleCameraMove(delta);
    if (objectRef.current) {
      objectRef.current.lookAt(camera.position);
    }
  });

  useEffect(() => {
    if (panorama && pathId) {
      const pidIndex = pathId.findIndex((p) => p === pid);
      const itemIndex = pathId.findIndex((p) => p === panorama.id);
      if (pidIndex !== -1 && itemIndex !== -1) {
        if (pidIndex < itemIndex) {
          setReverse(false);
        } else if (pidIndex > itemIndex) {
          setReverse(true);
        } else {
          setReverse(false);
        }
      } else {
        setReverse(false);
      }
    }
  }, [pid]);

  return (
    <mesh
      name="ResponsivePositionMarker"
      ref={objectRef}
      position={[position[0], position[1], position[2]]}
      scale={[0.3, 0.3, 0.3]}
      renderOrder={999}
      visible={true}
      onClick={!show ? (e) => {} : handleMarkerClick}
    >
      <boxGeometry args={[1, 1, 0.1]} />
      {/* FIXME 클릭 영역 볼 때 opacity 조정 */}
      <meshBasicMaterial transparent opacity={0} depthTest depthWrite />
      {show &&
        (buttonText ? (
          <Suspense fallback={null}>
            <HotspotShapeWidget
              color={isHovered ? palette.avnfrMain : palette.opacityBlack40}
              iconType={external ? buttonType! : 'default'}
              isConnected={connect ? true : external ? true : false}
              floorName={buttonText}
            ></HotspotShapeWidget>
          </Suspense>
        ) : (
          <Suspense fallback={null}>
            <HotspotShapeWidget
              color={isHovered ? palette.avnfrMain : palette.opacityBlack40}
              iconType={'default'}
              isConnected={connect ? true : external ? true : false}
              reverse={reverse}
            ></HotspotShapeWidget>
          </Suspense>
        ))}
    </mesh>
  );
}
